import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { FloatingSaveBar } from './save-bar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../primitives';

export interface ShellNavItem {
  name: string;
  href: string;
  badge?: string | number;
  /** Match the route exactly — use for the index route ('/'). */
  end?: boolean;
}

export interface ShellUser {
  name: string;
  email?: string;
  initials?: string;
}

export interface ShellUserMenuItem {
  label: string;
  onSelect: () => void;
}

/** Contextual "unsaved changes" bar shown in the top bar (Shopify-style). */
export interface ShellSaveBar {
  message?: React.ReactNode;
  /** Save action in progress. */
  saving?: boolean;
  saveDisabled?: boolean;
  /** Discard in progress — hides the Discard button (matches legacy SaveBar). */
  discardLoading?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
}

export interface AppShellProps {
  brand?: React.ReactNode;
  nav?: ShellNavItem[];
  footerNav?: ShellNavItem[];
  user?: ShellUser;
  /**
   * Grouped entries for the user dropdown (groups render with separators).
   * Without it the user area is a static display.
   */
  userMenu?: ShellUserMenuItem[][];
  /** When set, the top bar switches to the contextual unsaved-changes bar. */
  saveBar?: ShellSaveBar;
  /**
   * What is waiting on the seller, stated in the bar so it follows them off the
   * dashboard — e.g. "2 need attention".
   */
  attention?: React.ReactNode;
  children?: React.ReactNode;
}

const DEFAULT_NAV: ShellNavItem[] = [
  { name: 'Dashboard', href: '/', end: true },
  { name: 'Products', href: '/products' },
  { name: 'Orders', href: '/orders' },
  { name: 'Shipping', href: '/delivery' },
  { name: 'Payouts', href: '/payouts' },
  { name: 'Settings', href: '/settings/profile' },
];

const DEFAULT_FOOTER_NAV: ShellNavItem[] = [
  { name: 'Documentation', href: '/documentation' },
];

/**
 * A destination in the top bar.
 *
 * The app has six destinations, which fit in a bar — and the ~240px the
 * sidebar used to hold them is worth more to a table than to six links.
 */
function NavRow({ item }: { item: ShellNavItem }) {
  return (
    <NavLink
      to={item.href}
      end={item.end}
      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) =>
        cn(
          // No icons: five words are faster to read than five pictures, and the
          // icons were decoration on labels that already said the same thing.
          'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
          isActive
            ? 'bg-primary/10 font-semibold text-primary'
            : 'font-medium text-muted-foreground hover:bg-muted hover:text-foreground',
        )
      }
    >
      <span className="truncate">{item.name}</span>
      {item.badge != null && (
        <span className="ml-0.5 inline-flex items-center rounded-full bg-card px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

// forwardRef so Radix DropdownMenuTrigger can attach via asChild.
const UserButton = React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<'button'> & { user: ShellUser }>(
  ({ user, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-testid="user-menu-trigger"
      // The account is an initialled disc, not a block of text: whose account
      // this is was never in question, and the space belongs to the page.
      className="flex shrink-0 items-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      {...props}
    >
      <span className="sr-only">{user.name}</span>
      <span
        aria-hidden="true"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground"
      >
        {user.initials ?? user.name.charAt(0)}
      </span>
    </button>
  ),
);
UserButton.displayName = 'UserButton';

export function AppShell({
  brand = 'Acme Threads',
  nav = DEFAULT_NAV,
  footerNav = DEFAULT_FOOTER_NAV,
  user,
  userMenu,
  saveBar,
  attention,
  children,
}: AppShellProps) {
  const menuGroups = (userMenu ?? []).filter((group) => group.length > 0);

  return (
    <div className="flex min-h-screen flex-col bg-surface text-foreground">
      {/* One bar: brand, destinations, account. The save bar no longer replaces
          it — taking the navigation away mid-edit hid the very things someone
          might want to check before saving. */}
      <header className="sticky top-0 z-20 shrink-0 border-b border-border bg-card">
        <div className="flex h-12 items-center gap-2 px-5">
          <div className="mr-2 flex shrink-0 items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-[3px] bg-primary" aria-hidden="true" />
            <span className="hidden truncate text-[15px] font-bold tracking-tight sm:block">
              {brand}
            </span>
          </div>

          <nav aria-label="Main" className="min-w-0 flex-1">
            <ul className="flex items-center gap-1 overflow-x-auto">
              {nav.map((item) => (
                <li key={item.href} className="shrink-0">
                  <NavRow item={item} />
                </li>
              ))}
            </ul>
          </nav>

          {attention}

          {user &&
            (menuGroups.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <UserButton user={user} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuGroups.map((group, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      {groupIndex > 0 && <DropdownMenuSeparator />}
                      {group.map((item) => (
                        <DropdownMenuItem key={item.label} onSelect={item.onSelect}>
                          {item.label}
                        </DropdownMenuItem>
                      ))}
                    </React.Fragment>
                  ))}
                  {footerNav.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      {footerNav.map((item) => (
                        <DropdownMenuItem key={item.href} asChild>
                          <NavLink to={item.href}>{item.name}</NavLink>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <UserButton user={user} />
            ))}
        </div>
      </header>

      <main className="min-w-0 flex-1 bg-surface">{children}</main>

      {saveBar && (
        <FloatingSaveBar
          message={saveBar.message}
          saving={saveBar.saving}
          saveDisabled={saveBar.saveDisabled}
          discardLoading={saveBar.discardLoading}
          onSave={saveBar.onSave}
          onDiscard={saveBar.onDiscard}
        />
      )}
    </div>
  );
}
